import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, FileText, Mail, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EmailTabNav, { EMAIL_TEMPLATES_PATH, EMAIL_TRIGGER_PATH } from './EmailTabNav';

/**
 * Landing hub for the Email area. Two cards branch into the template library and the send
 * surface; both tabs are also reachable from the shared tab nav.
 */
function EmailHubComponent() {
  const navigate = useNavigate();

  const cards = [
    {
      key: 'templates',
      label: 'Email Configuration',
      description: 'Author and preview HTML email templates saved in the database',
      icon: <FileText size={18} />,
      accent: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
      path: EMAIL_TEMPLATES_PATH,
    },
    {
      key: 'trigger',
      label: 'Email Trigger',
      description: 'Compose, send, draft and review mail across folders',
      icon: <Send size={18} />,
      accent: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
      path: EMAIL_TRIGGER_PATH,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <EmailTabNav />

      <div className="mb-8 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Mail size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Email</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Template configuration and the mail trigger surface
          </p>
        </div>
      </div>

      <div className="grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
        {cards.map((card, idx) => (
          <motion.button
            key={card.key}
            type="button"
            onClick={() => navigate(card.path)}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1 + idx * 0.05 }}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.98 }}
            className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-left transition hover:border-cyan-500/40 hover:bg-slate-900"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${card.accent}`}>
                {card.icon}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-100">{card.label}</p>
                <p className="text-[11px] font-semibold text-slate-500">{card.description}</p>
              </div>
            </div>
            <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-600 transition group-hover:translate-x-0.5 group-hover:text-cyan-400" />
          </motion.button>
        ))}
      </div>
    </div>
  );
}

export default EmailHubComponent;
